import baseApi, { GenericResponse } from 'services/api/base';
import getProductKey from 'helper/requests/getProductKey';
import { SaveFieldsLocationParams } from 'types/Meteorology/Fields/TrackingForecast/Register/getFieldsLocations';
import { FieldsCity } from 'types/Meteorology/Fields/TrackingForecast/Register/getCities';
import {
  transformGetFieldsLocationsResponse,
  transformGetFieldsGroupsResponse,
  transformGetCitiesResponse,
} from 'helper/Meteorology/Fields/TrackingForecast/Register';

import type Group from 'types/Locations/Group';
import type Location from 'types/Locations/Location';

export const fieldsRegisterApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getFieldsCities: build.query<FieldsCity[], void>({
      query: () => {
        const productKey = getProductKey('meteorologia');
        return {
          url: `produtos/local-previsao/cidade/list?product_key=${productKey}`,
          method: 'GET',
        };
      },
      transformResponse: transformGetCitiesResponse,
    }),
    getFieldsGroups: build.query<Group[], void>({
      query: () => {
        const productKey = getProductKey('meteorologia');
        return {
          url: `produtos/local-previsao/grupo/list?product_key=${productKey}`,
          method: 'GET',
        };
      },
      transformResponse: transformGetFieldsGroupsResponse,
      providesTags: ['FieldsGroups'],
    }),
    getFieldsLocations: build.query<Location[], void>({
      query: () => {
        const productKey = getProductKey('meteorologia');
        return {
          url: `produtos/local-previsao/local/list?product_key=${productKey}`,
          methood: 'GET',
        };
      },
      transformResponse: transformGetFieldsLocationsResponse,
      providesTags: ['FieldsLocations'],
    }),
    saveFieldsGroup: build.mutation<GenericResponse<any>, { name: string, description: string }>({
      query: ({ name, description }) => {
        const productKey = getProductKey('meteorologia');

        return {
          url: `produtos/local-previsao/grupo/create?product_key=${productKey}`,
          method: 'POST',
          body: {
            nome: name,
            descricao: description,
          },
        };
      },
      invalidatesTags: ['FieldsGroups'],
    }),
    updateFieldsGroup: build.mutation<GenericResponse<any>, { id: number, name: string }>({
      query: ({
        id, name,
      }) => {
        const productKey = getProductKey('meteorologia');

        return {
          url: `produtos/local-previsao/grupo/update?product_key=${productKey}`,
          method: 'POST',
          body: {
            id,
            nome: name,
          },
        };
      },
      invalidatesTags: ['FieldsGroups'],
    }),
    saveFieldsLocation: build.mutation<GenericResponse<any>, SaveFieldsLocationParams>({
      query: ({
        groupId, name, latitude, longitude, cityId,
      }) => {
        const productKey = getProductKey('meteorologia');
        return {
          url: `produtos/local-previsao/local/create?product_key=${productKey}`,
          method: 'POST',
          body: {
            grupo_id: groupId,
            nome: name,
            latitude,
            longitude,
            city_id: cityId,
          },
        };
      },
      invalidatesTags: ['FieldsLocations'],
    }),
    updateFieldsLocation: build.mutation<GenericResponse<any>,
    { id: number, name: string }>({
      query: ({
        id, name,
      }) => {
        const productKey = getProductKey('meteorologia');

        return {
          url: `produtos/local-previsao/local/update?product_key=${productKey}`,
          method: 'POST',
          body: {
            id,
            nome: name,
          },
        };
      },
      invalidatesTags: ['FieldsLocations'],
    }),
    deleteFieldsGroup: build.mutation<GenericResponse<null>, { id: number }>({
      query: ({ id }) => {
        const productKey = getProductKey('meteorologia');

        return {
          url: `produtos/local-previsao/grupo/delete?product_key=${productKey}`,
          method: 'DELETE',
          body: {
            id,
          },
        };
      },
      invalidatesTags: ['FieldsGroups'],
    }),
    deleteFieldsLocation: build.mutation<GenericResponse<null>, { id: number }>({
      query: ({ id }) => {
        const productKey = getProductKey('meteorologia');

        return {
          url: `produtos/local-previsao/local/delete?product_key=${productKey}`,
          method: 'DELETE',
          body: {
            id,
          },
        };
      },
      invalidatesTags: ['FieldsLocations'],
    }),
  }),
});

export const {
  useGetFieldsCitiesQuery,
  useGetFieldsGroupsQuery,
  useGetFieldsLocationsQuery,
  useSaveFieldsGroupMutation,
  useSaveFieldsLocationMutation,
  useUpdateFieldsGroupMutation,
  useUpdateFieldsLocationMutation,
  useDeleteFieldsGroupMutation,
  useDeleteFieldsLocationMutation,
} = fieldsRegisterApi;
